import React, { Component } from "react";
import { Card, Form, Button, Spinner, Alert, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import AscModal from "../Modal/Modal";
import InlineForm from "../FromGroup/InlineForm";
import "./MailSettingModal.css";

// 【#501】プレビューは実物（lambdas/device-active-check/Functions/mail.js）と
// 同じ規則で「案内してよい値か」を判定する。実行環境が違うため共通化できず、
// 同じ規則を別実装で再現している。片方だけ変えるとプレビューと実物がずれる。
const OWN_SERVICE_DOMAIN = "rec-ace.com";
const OWN_COMPANY_VALUES = ["株式会社アセンド", "0120-993-124"];
const PLACEHOLDER_PATTERN = new RegExp("[xXｘＸ]{3,}");

/** 自社サービスのドメインか（default 行はアセンド自身の設定なので自社として扱う） */
const isOwnServiceDomain = (domain) => {
    // 【#501】domain 未設定は「OEM と断定できない」ため自社扱いにする。
    // hm12_companies.domain が NULL の会社は本番に 2 社（72 名）あり、これを
    // OEM 扱いにすると会社名・番号を登録しても文面へ出せなくなる（登録経路も無い）。
    // 未設定を解消するには hm12_companies.domain の登録が先に要る。
    if (!domain) return true;
    const normalized = String(domain).trim().toLowerCase();
    if (normalized === "default") return true;
    return normalized === OWN_SERVICE_DOMAIN
        || normalized.endsWith("." + OWN_SERVICE_DOMAIN);
};

/** 未登録・空白のみ・仮の値・OEM ドメインでのアセンドの値を null に寄せる */
const normalizeContactSetting = (value, ownDomain) => {
    if (value === undefined || value === null) return null;
    const trimmed = String(value).trim();
    if (trimmed === "") return null;
    if (PLACEHOLDER_PATTERN.test(trimmed)) return null;
    if (!ownDomain && OWN_COMPANY_VALUES.includes(trimmed)) return null;
    return trimmed;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_NUMBER_REGEX = /^[\d-]+$/;

export default class MailSettingModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            show: props.show || false,
            modalType: "setting",
            isLoading: false,
            error: null,
            // ドメイン一覧
            domainList: [],
            selectedDomain: "",
            isFallback: false,
            // メール設定値
            mail_from_local: "",
            mail_from_domain: "",
            mail_subject_prefix: "",
            mail_body_service_name: "",
            mail_body_company_name: "",
            mail_body_contact_number: "",
            // テスト送信
            showTestModal: false,
            testMailTo: "",
            isSendingTest: false
        };
    }

    componentDidMount() {
        if (this.props.show) {
            this.loadDomainList();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.show !== this.props.show && this.props.show) {
            this.setState({ show: true });
            this.loadDomainList();
        }
    }

    /**
     * ドメイン一覧取得
     */
    loadDomainList = async () => {
        this.setState({ isLoading: true, error: null });
        try {
            const response = await this.props.ascAxios('post', 'MailSetting/getMailDomainList', {});
            const domains = response.data.domains || [];
            const isFallback = response.data.isFallback || false;
            this.setState({
                domainList: domains,
                selectedDomain: domains.length > 0 ? domains[0] : "",
                isFallback,
                isLoading: false
            });
            if (domains.length > 0) {
                this.loadMailSettings(domains[0]);
            }
        } catch (err) {
            console.error(err);
            this.setState({ isLoading: false, error: "ドメイン一覧の取得に失敗しました" });
        }
    };

    /**
     * メール設定取得
     */
    loadMailSettings = async (parameter_type) => {
        this.setState({ isLoading: true, error: null });
        try {
            const response = await this.props.ascAxios('post', 'MailSetting/getMailSettings', {
                parameter_type
            });
            const settings = response.data || {};
            const fullAddress = settings.MAIL_FROM_ADDRESS || "";
            const atIdx = fullAddress.lastIndexOf('@');
            this.setState({
                mail_from_local: atIdx >= 0 ? fullAddress.substring(0, atIdx) : fullAddress,
                mail_from_domain: atIdx >= 0 ? fullAddress.substring(atIdx + 1) : "",
                mail_subject_prefix: settings.MAIL_SUBJECT_PREFIX || "",
                mail_body_service_name: settings.MAIL_BODY_SERVICE_NAME || "",
                mail_body_company_name: settings.MAIL_BODY_COMPANY_NAME || "",
                mail_body_contact_number: settings.MAIL_BODY_CONTACT_NUMBER || "",
                isLoading: false
            });
        } catch (err) {
            console.error(err);
            this.setState({ isLoading: false, error: "メール設定の取得に失敗しました" });
        }
    };

    /**
     * ドメイン選択変更
     */
    handleDomainChange = (e) => {
        const domain = e.target.value;
        this.setState({ selectedDomain: domain });
        this.loadMailSettings(domain);
    };

    /**
     * 入力フィールド変更
     */
    handleInputChange = (field) => (e) => {
        this.setState({ [field]: e.target.value });
    };

    /**
     * 保存処理
     */
    handleSave = async () => {
        this.setState({ isLoading: true, error: null });
        try {
            await this.props.ascAxios('post', 'MailSetting/updateMailSettings', {
                parameter_type: this.state.selectedDomain,
                mail_from_address: this.getFullFromAddress(),
                mail_subject_prefix: this.state.mail_subject_prefix,
                mail_body_service_name: this.state.mail_body_service_name,
                mail_body_company_name: this.state.mail_body_company_name,
                mail_body_contact_number: this.state.mail_body_contact_number
            });
            this.setState({ isLoading: false });
            alert(this.props.langText.Message.DataSettingSuccess);
            this.props.onHide();
        } catch (err) {
            console.error(err);
            this.setState({ isLoading: false });
            alert((err.response && err.response.data && err.response.data.error) || this.props.langText.Message.DataSettingError);
        }
    };

    /**
     * テスト送信モーダル表示
     */
    handleShowTestDialog = () => {
        this.setState({ showTestModal: true, testMailTo: "", error: null });
    };

    /**
     * テストメール送信
     */
    handleSendTestMail = async () => {
        if (!this.state.testMailTo || !EMAIL_REGEX.test(this.state.testMailTo)) return;
        this.setState({ isSendingTest: true, error: null });
        try {
            await this.props.ascAxios('post', 'MailSetting/sendTestMail', {
                to: this.state.testMailTo,
                parameter_type: this.state.selectedDomain,
                mail_from_address: this.getFullFromAddress(),
                mail_subject_prefix: this.state.mail_subject_prefix,
                mail_body_service_name: this.state.mail_body_service_name,
                mail_body_company_name: this.state.mail_body_company_name,
                mail_body_contact_number: this.state.mail_body_contact_number
            });
            this.setState({
                isSendingTest: false,
                showTestModal: false
            });
            alert(this.props.langText.Message.TestMailSuccess);
        } catch (err) {
            console.error(err);
            this.setState({ isSendingTest: false });
            alert((err.response && err.response.data && err.response.data.error) || this.props.langText.Message.TestMailFailed);
        }
    };

    /**
     * プレビュー本文を組み立てる。
     *
     * 【#501】未登録のときの実際の文面に合わせる。
     * 以前はプレビューだけ「会社名」「000-0000-0000」というダミーを出していたが、
     * 実際の送信では会社名の行が消え、連絡先は販売会社への誘導へ変わる
     * （lambdas/device-active-check/Functions/mail.js の buildOfflineBody）。
     * プレビューと実物がずれていると、未登録のまま運用に入る判断を誤らせる。
     *
     * 描画から切り出しているのはテストのため。
     *
     * 【文面を変えるときの注意】実行環境が違う（ブラウザ / Lambda）ため
     * buildOfflineBody と共通化できず、同じ分岐を別実装で再現している。
     * 片方だけ変えるとプレビューと実物がずれる。必ず両方を追随させること。
     *
     * @returns {string} 本文（プレビュー用にシリアルナンバーは固定の伏せ字）
     */
    buildPreviewBody = () => {
        const {
            mail_body_service_name, mail_body_company_name, mail_body_contact_number, selectedDomain
        } = this.state;
        // 実物と同じ判定を通す。空白のみ・仮の値（0120-xxx-xxx）・OEM ドメインでの
        // アセンドの値は、実物では未登録として扱われる。
        const ownDomain = isOwnServiceDomain(selectedDomain);
        const companyName = normalizeContactSetting(mail_body_company_name, ownDomain);
        const contactNumber = normalizeContactSetting(mail_body_contact_number, ownDomain);
        // 会社名が無いと「のサポート窓口です。」だけが残るため行ごと落とす
        const greetingLine = companyName
            ? `${companyName}のサポート窓口です。\n`
            : "";
        const contactLine = contactNumber
            ? `お手数ですがご連絡（${contactNumber}）いただけますと幸いです。`
            : "お手数ですが販売会社までご連絡お願いいたします。";

        return `${mail_body_service_name || "サービス名"}ご利用のお客様

平素より弊社通話録音サービスをご利用いただき、誠にありがとうございます。
${greetingLine}
昨日時点で、お客様先に設置されている
シリアルナンバー（XXXX-XXXX-XXXX）の機器との接続が
確認できない状況となっております。
大変お手数をおかけいたしますが、
添付の図にある録音生成機器の電源を一度抜き差ししていただけますでしょうか。
なお、電源の抜き差し後、SYSランプが点滅以外の状態になっている場合は、
${contactLine}
何卒よろしくお願い申し上げます。`;
    };

    /**
     * プレビュー生成
     */
    renderPreview = () => {
        const { mail_subject_prefix } = this.state;
        const fromAddress = this.getFullFromAddress();
        return (
            <Card className="mt-3 mail-setting-preview-card" bg="light">
                <Card.Body style={{ fontSize: "0.85em", fontFamily: "monospace" }}>
                    <div><strong>From:</strong> {fromAddress || "noreply@mail.example.com"}</div>
                    <div><strong>Subject:</strong> 【{mail_subject_prefix || "サービス名"}】デバイスがオフラインです</div>
                    <hr />
                    <div style={{ whiteSpace: "pre-wrap" }}>{this.buildPreviewBody()}</div>
                    {/* 【#501】default 行はアセンド自身の設定として表示するが、これを継承する
                        OEM 企業（MAIL 行を持たない会社）の実メールでは会社名・番号が落ちる。
                        プレビューだけを見て「OEM にもこの文面が届く」と誤解しないよう注記する。 */}
                    {this.state.selectedDomain === "default" && (
                        <div className="mt-2 text-muted mail-setting-preview-note">
                            ※ この設定を継承する OEM 企業（自社ドメイン以外）では、
                            会社名・連絡先番号は案内に使われません。
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    };

    /**
     * 送信元メールアドレス（フル）取得
     */
    getFullFromAddress = () => {
        const { mail_from_local, mail_from_domain } = this.state;
        if (!mail_from_local && !mail_from_domain) return "";
        if (!mail_from_domain) return mail_from_local;
        return `${mail_from_local}@${mail_from_domain}`;
    };

    /**
     * 表示用ドメイン（dev./demo.プレフィックス除去）
     */
    getDisplayDomain = (domain) => {
        if (!domain) return '';
        // 先頭のdev./demo.を除去
        let result = domain.replace(/^(?:dev|demo)\./, '');
        // 中間のdev./demo.を繰り返し除去（多重ネスト対応）
        while (/\.(?:dev|demo)\./.test(result)) {
            result = result.replace(/\.(?:dev|demo)\./, '.');
        }
        return result;
    };

    validationHandle = () => {
        const { mail_from_local, mail_from_domain } = this.state;
        if (!mail_from_local && !mail_from_domain) return false;
        if (mail_from_local && !mail_from_domain) return false;
        return true;
    };

    render() {
        const { langText } = this.props;

        return (
            <AscModal
                state={this.state}
                propSetState={(newState) => {
                    if (newState.show === false) {
                        this.setState({ show: false });
                        setTimeout(() => {
                            this.setState({
                                error: null,
                                showTestModal: false,
                                mail_from_local: "",
                                mail_from_domain: "",
                                mail_subject_prefix: "",
                                mail_body_service_name: "",
                                mail_body_company_name: "",
                                mail_body_contact_number: "",
                                testMailTo: "",
                                isSendingTest: false
                            });
                            this.props.onHide();
                        }, 150);
                    } else {
                        this.setState(newState);
                    }
                }}
                langText={langText}
                validationHandle={this.validationHandle}
                onClick={(modalType) => () => {
                    if (modalType === "setting") {
                        this.handleSave();
                    }
                }}
            >
                <Card variant="info">
                    <Card.Header>
                        <Card.Title>メール送信設定</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {this.state.error && (
                            <Alert variant="danger" onClose={() => this.setState({ error: null })} dismissible>
                                {this.state.error}
                            </Alert>
                        )}
                        {this.state.isLoading ? (
                            <div className="text-center p-4"><Spinner animation="border" /></div>
                        ) : (
                            <>
                                <InlineForm key="mail_domain" controlId="mail_domain" label="対象ドメイン">
                                    <Form.Select value={this.state.selectedDomain} onChange={this.handleDomainChange}>
                                        {this.state.domainList.map(domain => (
                                            <option key={domain} value={domain}>{domain}</option>
                                        ))}
                                    </Form.Select>
                                    {this.state.isFallback && (
                                        <Form.Text className="text-warning">
                                            ※ 自社ドメインの設定が未登録のため、デフォルト設定を表示しています
                                        </Form.Text>
                                    )}
                                </InlineForm>

                                <hr />
                                <h6>送信元・件名</h6>
                                <InlineForm key="mail_from_address" controlId="mail_from_address" label="送信元メールアドレス">
                                    {this.state.mail_from_domain ? (
                                        <div className="d-flex align-items-center gap-1">
                                            <Form.Control
                                                type="text"
                                                value={this.state.mail_from_local}
                                                onChange={(e) => this.setState({ mail_from_local: e.target.value })}
                                                placeholder="noreply"
                                            />
                                            <span className="text-nowrap text-muted">@{this.getDisplayDomain(this.state.mail_from_domain)}</span>
                                        </div>
                                    ) : (
                                        <Form.Control
                                            type="email"
                                            value={this.state.mail_from_local}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const atIdx = val.lastIndexOf('@');
                                                if (atIdx >= 0) {
                                                    this.setState({ mail_from_local: val.substring(0, atIdx), mail_from_domain: val.substring(atIdx + 1) });
                                                } else {
                                                    this.setState({ mail_from_local: val });
                                                }
                                            }}
                                            placeholder="noreply@mail.example.com"
                                        />
                                    )}
                                </InlineForm>
                                <InlineForm key="mail_subject_prefix" controlId="mail_subject_prefix" label="件名プレフィックス">
                                    <Form.Control type="text" value={this.state.mail_subject_prefix} onChange={this.handleInputChange("mail_subject_prefix")} placeholder="サービス名" maxLength={64} />
                                    <Form.Text className="text-muted">
                                        件名は「【{this.state.mail_subject_prefix || "サービス名"}】デバイスがオフラインです」の形式で送信されます
                                    </Form.Text>
                                </InlineForm>

                                <hr />
                                <h6>メール本文（会社情報）</h6>
                                <InlineForm key="mail_body_service_name" controlId="mail_body_service_name" label="サービス名">
                                    <Form.Control type="text" value={this.state.mail_body_service_name} onChange={this.handleInputChange("mail_body_service_name")} placeholder="サービス名" maxLength={64} />
                                </InlineForm>
                                <InlineForm key="mail_body_company_name" controlId="mail_body_company_name" label="会社名">
                                    <Form.Control type="text" value={this.state.mail_body_company_name} onChange={this.handleInputChange("mail_body_company_name")} placeholder="会社名" maxLength={128} />
                                </InlineForm>
                                <InlineForm key="mail_body_contact_number" controlId="mail_body_contact_number" label="連絡先番号">
                                    <Form.Control
                                        type="text"
                                        value={this.state.mail_body_contact_number}
                                        onChange={this.handleInputChange("mail_body_contact_number")}
                                        placeholder="000-0000-0000"
                                        isInvalid={!!this.state.mail_body_contact_number && !CONTACT_NUMBER_REGEX.test(this.state.mail_body_contact_number)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        連絡先番号はハイフン区切りの数字で入力してください
                                    </Form.Control.Feedback>
                                </InlineForm>

                                <hr />
                                <h6>プレビュー</h6>
                                {this.renderPreview()}

                                <div className="text-end mt-3">
                                    <Button className="ok-execute mail-setting-test-btn" onClick={this.handleShowTestDialog}>
                                        テスト送信
                                    </Button>
                                </div>

                                <div className="mt-3">
                                    <Form.Text className="text-muted">
                                        ※ 送信元ドメインの変更にはSES認証が必要です。インフラ担当にご連絡ください。
                                    </Form.Text>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>

                {/* テスト送信モーダル（RecordTagEditModalと同じパターン） */}
                <Modal
                    show={this.state.showTestModal}
                    size="md"
                    className="mail-test-send-modal"
                    backdrop="static"
                    centered
                    backdropClassName="mail-test-send-backdrop"
                >
                    <Form horizontal="true">
                        <Modal.Header className="mail-test-send-header">
                            <Modal.Title>テストメール送信</Modal.Title>
                            <Button
                                className="x-execute"
                                onClick={() => this.setState({ showTestModal: false })}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </Button>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>送信先メールアドレス</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={this.state.testMailTo}
                                    onChange={(e) => this.setState({ testMailTo: e.target.value })}
                                    placeholder="example@example.com"
                                    // 未入力時はエラー表示しない（入力済みかつ形式不正の場合のみ表示）
                                    isInvalid={!!this.state.testMailTo && !EMAIL_REGEX.test(this.state.testMailTo)}
                                />
                                <Form.Control.Feedback type="invalid">
                                    有効なメールアドレスを入力してください
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Text className="text-muted">
                                ※ 現在の設定内容でテストメールを送信します（件名に【TEST】が付与され、シリアルナンバーにはダミー値が使用されます）
                            </Form.Text>
                        </Modal.Body>
                        <Modal.Footer className="card-button">
                            <Button
                                className="cancel-execute"
                                variant="outline-secondary"
                                onClick={() => this.setState({ showTestModal: false })}
                            >
                                {langText.Body.Cancel}
                            </Button>
                            <Button
                                className="ok-execute"
                                onClick={this.handleSendTestMail}
                                // 未入力・形式不正どちらも無効化（isInvalidと条件が異なるのは意図的）
                                disabled={!this.state.testMailTo || !EMAIL_REGEX.test(this.state.testMailTo) || this.state.isSendingTest}
                            >
                                {this.state.isSendingTest ? (
                                    <Spinner animation="border" size="sm" />
                                ) : langText.Body.Submit}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </AscModal>
        );
    }
}
